import {
  AlertTriangle,
  Ban,
  Circle,
  CircleAlert,
  CircleCheck,
  CircleSlash,
  Clock,
  EyeOff,
  type LucideIcon,
  Pause,
  ShieldAlert,
} from 'lucide-react';

import type { BadgeTone } from '@/components/admin';
import { adminCopy } from '@/lib/copy';

import type {
  AssignmentStatus,
  ClientUserStatus,
  FlagType,
  RedFlagStatus,
  RiskProfile,
} from '../types';
import type { ClientDisplayType } from '../utils/clientDisplayType';

interface StatusEntry {
  label: string;
  tone: BadgeTone;
  icon?: LucideIcon;
}

export const clientUserStatusMap: Record<ClientUserStatus, StatusEntry> = {
  ACTIVE: {
    label: adminCopy.clients.statusMap.activeLabel,
    tone: 'primary',
    icon: Circle,
  },
  PENDING_INVITE: {
    label: adminCopy.clients.statusMap.pendingInviteLabel,
    tone: 'info',
    icon: Clock,
  },
  PENDING_VERIFICATION: {
    label: adminCopy.clients.statusMap.pendingVerificationLabel,
    tone: 'warning',
    icon: Clock,
  },
  DISABLED: {
    label: adminCopy.clients.statusMap.disabledLabel,
    tone: 'danger',
    icon: Ban,
  },
  SHADOW: {
    label: adminCopy.clients.statusMap.dormantLabel,
    tone: 'neutral',
    icon: EyeOff,
  },
};

export const clientTypeMap: Record<ClientDisplayType, StatusEntry> = {
  student: { label: adminCopy.clients.typeMap.student, tone: 'info' },
  corporate: { label: adminCopy.clients.typeMap.corporate, tone: 'neutral' },
  staff: { label: adminCopy.clients.typeMap.staff, tone: 'warning' },
  faculty: { label: adminCopy.clients.typeMap.faculty, tone: 'success' },
};

export const riskProfileMap: Record<RiskProfile, StatusEntry> = {
  none: { label: adminCopy.clients.riskMap.none, tone: 'neutral' },
  unknown: { label: adminCopy.clients.riskMap.unknown, tone: 'neutral' },
  // Retired by migration 0068 (every row moved to `low`). Kept so a stale record
  // from an older build still renders a chip instead of blowing up the lookup.
  very_low: { label: adminCopy.clients.riskMap.veryLow, tone: 'success' },
  low: { label: adminCopy.clients.riskMap.low, tone: 'success' },
  medium: { label: adminCopy.clients.riskMap.medium, tone: 'info' },
  high: { label: adminCopy.clients.riskMap.high, tone: 'warning', icon: AlertTriangle },
  very_high: {
    label: adminCopy.clients.riskMap.veryHigh,
    tone: 'danger',
    icon: ShieldAlert,
  },
  critical: {
    label: adminCopy.clients.riskMap.critical,
    tone: 'danger',
    icon: ShieldAlert,
  },
};

export const flagTypeMap: Record<FlagType, StatusEntry> = {
  crisis: {
    label: adminCopy.clients.flagTypeMap.crisis,
    tone: 'danger',
    icon: ShieldAlert,
  },
  acute_risk: {
    label: adminCopy.clients.flagTypeMap.acuteRisk,
    tone: 'warning',
    icon: CircleAlert,
  },
};

export const redFlagStatusMap: Record<RedFlagStatus, StatusEntry> = {
  open: { label: adminCopy.clients.flagStatusMap.open, tone: 'danger', icon: CircleAlert },
  under_review: {
    label: adminCopy.clients.flagStatusMap.underReview,
    tone: 'warning',
    icon: Pause,
  },
  closed: { label: adminCopy.clients.flagStatusMap.closed, tone: 'success', icon: CircleCheck },
};

export const assignmentStatusMap: Record<AssignmentStatus, StatusEntry> = {
  pending: {
    label: adminCopy.clients.assignmentStatusMap.pending,
    tone: 'neutral',
    icon: CircleSlash,
  },
  in_progress: {
    label: adminCopy.clients.assignmentStatusMap.inProgress,
    tone: 'info',
    icon: Clock,
  },
  completed: {
    label: adminCopy.clients.assignmentStatusMap.completed,
    tone: 'success',
    icon: CircleCheck,
  },
};
